import React, { useMemo } from 'react';
import { Card, Empty, Typography, Tag } from 'antd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import '../student/AIDatasetLessonPlayer.css';
import { TAIDatasetLessonConfig, TAIDatasetEntry } from '../../../models/courses/aiDataset';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';

type LanguageKey = 'fr' | 'en' | 'ar';

type AIDatasetDragItem = {
  id: string;
  label: string;
  expectedKey: string | null;
};

type AIDatasetTarget = {
  id: string;
  label: string;
};

interface AIDatasetLessonTeacherProps {
  config: TAIDatasetLessonConfig;
  statement?: string;
}

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const getFeaturesByLanguage = (entry: TAIDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const getNoiseByLanguage = (config: TAIDatasetLessonConfig, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
    case 'ar':
      return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
    default:
      return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
  }
};

const AIDatasetLessonTeacher: React.FC<AIDatasetLessonTeacherProps> = ({ config, statement }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');

  const { targets, assignments, availableItems } = useMemo(() => {
    const entries = Object.entries(config.dataset || {});
    const builtTargets: AIDatasetTarget[] = entries.map(([id, entry]) => ({
      id,
      label: entry.key,
    }));

    // Pre-fill all features in their correct key assignments
    const preFilledAssignments: Record<string, AIDatasetDragItem[]> = {};
    entries.forEach(([id, entry]) => {
      preFilledAssignments[id] = getFeaturesByLanguage(entry, language).map((feature, index) => ({
        id: `${id}-feature-${index}`,
        label: feature,
        expectedKey: id,
      }));
    });

    // Noise items remain in the pool
    const noiseItems: AIDatasetDragItem[] = getNoiseByLanguage(config, language).map((noise, index) => ({
      id: `noise-${index}`,
      label: noise,
      expectedKey: null,
    }));

    return {
      targets: builtTargets,
      assignments: preFilledAssignments,
      availableItems: noiseItems,
    };
  }, [config, language]);

  if (!targets.length) {
    return <Empty description={t('aiDataset.student.empty')} />;
  }

  const isImagePath = (v: string) => /\.(png|jpe?g|gif|svg|webp)$/i.test(v) || v.includes('/images/');
  const isAudioPath = (v: string) => /\.(mp3|wav|ogg|m4a)$/i.test(v) || v.includes('/sounds/') || v.includes('/audio/');

  const renderItem = (item: AIDatasetDragItem, index: number, isCorrect: boolean = true) => {
    const label = item.label || '';
    const showImage = label && isImagePath(label);
    const showAudio = label && isAudioPath(label);
    return (
      <Draggable draggableId={item.id} index={index} key={item.id} isDragDisabled={true}>
        {(provided) => (
          <div
            className={`ai-dataset-draggable ${isCorrect ? 'ai-dataset-draggable--correct' : ''}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
              ...provided.draggableProps.style,
              cursor: 'default',
              opacity: 0.9,
            }}
          >
            {showImage ? (
              <img
                src={getImagePath(label)}
                alt="feature"
                className="ai-dataset-draggable-media"
                draggable={false}
              />
            ) : showAudio ? (
              <audio
                src={getSoundPath(label)}
                controls
                className="ai-dataset-draggable-media"
                draggable={false}
                onPointerDown={(e) => e.stopPropagation()}
              />
            ) : (
              <Typography.Text ellipsis={{ tooltip: label }}>{label}</Typography.Text>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  const renderDropZone = (droppableId: string, items: AIDatasetDragItem[], isPool: boolean = false) => (
    <Droppable droppableId={droppableId} isDropDisabled={true}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          className={`ai-dataset-dropzone ${isPool ? 'ai-dataset-dropzone--pool' : 'ai-dataset-dropzone--filled'}`}
          {...provided.droppableProps}
        >
          {items.map((item, index) => renderItem(item, index, item.expectedKey !== null))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <DragDropContext onDragEnd={() => {}}>
      {statement && (
        <div className="ai-dataset-statement">
          {statement}
        </div>
      )}
      <div className="ai-dataset-player">
        <div className="ai-dataset-column ai-dataset-column--left">
          <Typography.Title level={4}>
            {t('aiDataset.student.keysTitle')}
            <Tag color="blue" style={{ marginLeft: 12, fontSize: 12 }}>
              {t('aiDataset.teacher.view')}
            </Tag>
          </Typography.Title>
          <div className="ai-dataset-keys">
            {targets.map((target) => {
              const label = target.label || '';
              const showImage = label && isImagePath(label);
              const showAudio = label && isAudioPath(label);
              return (
                <Card key={target.id} title={!showImage && !showAudio ? target.label : undefined} className="ai-dataset-key-card">
                  {showImage && (
                    <div className="ai-dataset-key-media">
                      <img src={getImagePath(label)} alt="key" draggable={false} />
                    </div>
                  )}
                  {showAudio && (
                    <div className="ai-dataset-key-media ai-dataset-key-media--audio">
                      <audio src={getSoundPath(label)} controls draggable={false} onPointerDown={(e) => e.stopPropagation()} />
                    </div>
                  )}
                  {renderDropZone(target.id, assignments[target.id] || [], false)}
                </Card>
              );
            })}
          </div>
        </div>

        <div className="ai-dataset-column ai-dataset-column--right">
          <Typography.Title level={4}>{t('aiDataset.student.optionsTitle')}</Typography.Title>
          <Card>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('aiDataset.teacher.instructions')}
            </Typography.Paragraph>
            {renderDropZone('pool', availableItems, true)}
          </Card>
        </div>
      </div>
    </DragDropContext>
  );
};

export default AIDatasetLessonTeacher;
